<template>
  <main>
    <v-container>
      <v-toolbar flat class="main-header mb-6">
        <v-toolbar-title class="main-header__title">
          <h1>Sub Inventories</h1>
        </v-toolbar-title>

        <v-spacer></v-spacer>
        <v-toolbar-items v-if="canAdd" class="main-header__contents">
          <v-btn
            class="my-auto mx-1 radius-12"
            color="success"
            @click="showAddEditDialog = true"
          >
            <v-icon>mdi-plus-circle</v-icon>
            <span class="mx-2"> Add </span>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <hr class="mb-6" />

      <v-data-table
        :headers="subInventoriesTableHeaders"
        :loading="loadingTable"
        :items="subInventories"
        :mobile-breakpoint="0"
        :items-per-page="200"
        hide-default-footer
        class="table"
      >
        <template v-slot:item="{ item, index }">
          <tr class="table__row">
            <td class="table__cell text-center">
              <span> {{ item.code }} </span>
            </td>

            <td class="table__cell name text-center">
              <span> {{ item.name || "-" }} </span>
            </td>

            <td class="table__cell name text-center">
              <v-chip v-if="item.active" color="success"> Yes </v-chip>
              <v-chip v-else color="error"> No </v-chip>
            </td>

            <td class="table__cell name text-center">
              <v-chip v-if="item.for_camps" color="success"> Yes </v-chip>
              <v-chip v-else color="error"> No </v-chip>
            </td>

            <td class="table__cell name text-center">
              <v-chip v-if="item.for_employees" color="success"> Yes </v-chip>
              <v-chip v-else color="error"> No </v-chip>
            </td>

            <td class="table__cell text-center">
              <div>
                <v-icon
                  v-if="canEdit"
                  title="Edit"
                  class="edit-icon"
                  :disabled="loadingAction"
                  @click="showSubInventoryEdit(index)"
                >
                  mdi-pencil
                </v-icon>

                <v-icon
                  v-if="canDelete"
                  title="Delete"
                  class="delete-icon mx-2"
                  :disabled="loadingAction"
                  @click="deleteSubInventory(item.id)"
                >
                  mdi-delete
                </v-icon>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
      <!-- pagination -->
      <div class="text-xs-center pt-2">
        <v-pagination
          v-if="pagination.last_page > 1"
          v-model="pagination.current_page"
          :length="pagination.last_page"
          :total-visible="20"
          @input="handlePagination"
        ></v-pagination>
      </div>
      <!-- pagination -->
    </v-container>

    <SCMSubInventoryAddEditDialogComponent
      :subInventoryInfo="subInventoryInfo"
      :loading="loadingAction"
      @saveSubInventory="saveSubInventory"
      @resetSubInventoryInfo="resetSubInventoryInfo"
      v-model="showAddEditDialog"
    >
      <v-alert v-if="globalServerErrors !== null" dense outlined type="error">
        <p
          class="mb-0"
          v-for="globalErr in globalServerErrors"
          :key="globalErr"
        >
          {{ globalErr[0] }}
        </p>
      </v-alert>
    </SCMSubInventoryAddEditDialogComponent>
  </main>
</template>

<script>
import { mapGetters } from "vuex";
import TableHeaders from "@/helpers/TableHeaders";
import {
  IndexData,
  StoreData,
  UpdateData,
  DeleteData
} from "@/helpers/apiMethods";
import { isEqual } from "lodash";
import SCMSubInventoryAddEditDialogComponent from "./components/add-edit-dialog.component";
import { th } from "date-fns/locale";

export default {
  components: {
    SCMSubInventoryAddEditDialogComponent
  },
  data() {
    return {
      subInventories: [],
      subInventoriesTableHeaders: [],
      pagination: {},
      queries: {},
      loadingTable: false,
      loadingAction: false,
      showAddEditDialog: false,
      subInventoryInfo: {
        code: "",
        name: "",
        active: 0,
        for_camps: 0,
        for_employees: 0
      },
      isEdit: false,
      globalServerErrors: null
    };
  },
  mounted() {
    this.setTableHeaders();
  },
  computed: {
    ...mapGetters({
      permissions: "permissions"
    }),
    canAdd() {
      return this.permissions.includes("store_api_admin-panel_scm_inventories");
    },
    canEdit() {
      return this.permissions.includes(
        "update_api_admin-panel_scm_inventories_{inventory}"
      );
    },
    canDelete() {
      return this.permissions.includes(
        "destroy_api_admin-panel_scm_inventories_{inventory}"
      );
    }
  },
  methods: {
    handlePagination(page) {
      this.queries = { ...this.queries, page };
      if (!isEqual(this.queries, this.$route.query)) {
        this.$router.push({ query: this.queries });
      }
    },
    setTableHeaders() {
      const headersList = [
        "Main Code",
        "Name",
        "Active",
        "Camp",
        "Employees",
        "Actions"
      ];
      this.subInventoriesTableHeaders = TableHeaders(headersList);
    },
    indexSubInventories(query) {
      this.loadingTable = true;
      IndexData({ reqName: "scm/inventories", query })
        .then(res => {
          const { data, meta } = res.data;
          this.subInventories = data;
          this.pagination = meta;
        })
        .catch(() => {
          console.log();
        })
        .finally(() => {
          this.loadingTable = false;
        });
    },
    showSubInventoryEdit(subInventoryIndex) {
      this.subInventoryInfo = { ...this.subInventories[subInventoryIndex] };
      this.showAddEditDialog = true;
    },
    saveSubInventory(subInventoryId) {
      this.loadingAction = true;
      subInventoryId
        ? this.editSubInventory(subInventoryId)
        : this.addSubInventory();
    },
    addSubInventory() {
      StoreData({ reqName: "scm/inventories", data: this.subInventoryInfo })
        .then(() => {
          this.showAddEditDialog = false;
          this.globalServerErrors = null;
          this.indexSubInventories(this.$route.query);
        })
        .catch(err => {
          this.globalServerErrors = err.response.data.errors;
        })
        .finally(() => {
          this.loadingAction = false;
        });
    },
    editSubInventory(subInventoryId) {
      UpdateData({
        reqName: "scm/inventories",
        data: { ...this.subInventoryInfo, _method: "put" },
        id: subInventoryId
      })
        .then(() => {
          this.showAddEditDialog = false;
          this.indexSubInventories(this.$route.query);
          this.globalServerErrors = null;
        })
        .catch(err => {
          this.globalServerErrors = err.response.data.errors;
        })
        .finally(() => {
          this.loadingAction = false;
        });
    },
    deleteSubInventory(subInventoryId) {
      this.popUp().then(value => {
        if (!value.dismiss) {
          this.loadingAction = true;
          DeleteData({ reqName: "scm/inventories", id: subInventoryId })
            .then(() => {
              this.indexSubInventories(this.$route.query);
            })
            .catch(() => {
              console.log();
            })
            .finally(() => {
              this.loadingAction = false;
            });
        }
      });
    },
    resetSubInventoryInfo() {
      this.subInventoryInfo = {
        code: "",
        name: "",
        active: 0,
        for_camps: 0,
        for_employees: 0
      };
      this.globalServerErrors = null;
    }
  },
  watch: {
    $route: {
      handler(route) {
        const { query } = route;
        this.queries = query;
        this.indexSubInventories(query);
      },
      immediate: true,
      deep: true
    }
  }
};
</script>
